import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { Trash2, UserPlus, Eye } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface Employee {
  id: string;
  email: string;
  role: string;
}

type AccountType = 'moderator' | 'order_manager' | 'content_manager' | 'user';

const AdminEmployees = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accountType, setAccountType] = useState<AccountType>('moderator');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch employees (moderators)
  const { data: employees = [], isLoading } = useQuery({
    queryKey: ['employees'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('list-employees');

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      // Filter to show only employees (not regular users)
      return (data.employees as Employee[]).filter(e => 
        e.role === 'admin' || e.role === 'moderator' || e.role === 'order_manager' || e.role === 'content_manager'
      );
    }
  });

  // Fetch user details
  const { data: userDetails, isLoading: isLoadingDetails } = useQuery({
    queryKey: ['user-details', selectedUserId],
    queryFn: async () => {
      if (!selectedUserId) return null;

      const { data, error } = await supabase.functions.invoke('get-user-details', {
        body: { userId: selectedUserId }
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      return data;
    },
    enabled: !!selectedUserId
  });

  // Create account mutation (employee or user)
  const createAccount = useMutation({
    mutationFn: async ({ email, password, type }: { email: string; password: string; type: AccountType }) => {
      const functionName = type === 'user' ? 'create-user' : 'create-employee';
      const body = type === 'user' ? { email, password } : { email, password, role: type };
      
      const { data, error } = await supabase.functions.invoke(functionName, {
        body
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      return data.user;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      setEmail("");
      setPassword("");
      toast({
        title: accountType === 'user' ? "Utilisateur créé" : "Employé créé",
        description: `Le compte a été créé avec succès`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erreur",
        description: error.message || "Impossible de créer le compte",
        variant: "destructive",
      });
    }
  });

  // Delete employee mutation
  const deleteEmployee = useMutation({
    mutationFn: async (userId: string) => {
      const { data, error } = await supabase.functions.invoke('delete-employee', {
        body: { userId }
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      toast({
        title: "Employé supprimé",
        description: "Le compte employé a été supprimé",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erreur",
        description: error.message || "Impossible de supprimer l'employé",
        variant: "destructive",
      });
    }
  });

  // Update role mutation
  const updateRole = useMutation({
    mutationFn: async ({ userId, newRole }: { userId: string; newRole: string }) => {
      const { data, error } = await supabase.functions.invoke('update-employee-role', {
        body: { userId, newRole }
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      toast({
        title: "Rôle modifié",
        description: "Le rôle de l'employé a été modifié avec succès",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erreur",
        description: error.message || "Impossible de modifier le rôle",
        variant: "destructive",
      });
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast({
        title: "Erreur",
        description: "Veuillez remplir tous les champs",
        variant: "destructive",
      });
      return;
    }
    createAccount.mutate({ email, password, type: accountType });
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <h1 className="text-3xl font-bold mb-8">Gestion des employés</h1>

      <Alert className="mb-6">
        <AlertDescription>
          <strong>Rôles disponibles :</strong>
          <ul className="mt-2 ml-4 list-disc space-y-1">
            <li><strong>Administrateur</strong> : Accès complet (gestion produits + employés + commandes + contenu)</li>
            <li><strong>Gestionnaire de commandes</strong> : Peut uniquement gérer les commandes</li>
            <li><strong>Gestionnaire de produits</strong> : Peut uniquement gérer les produits</li>
            <li><strong>Gestionnaire de contenu</strong> : Peut uniquement gérer les blogs, projets et événements</li>
          </ul>
        </AlertDescription>
      </Alert>


      {/* Create account form */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Créer un compte</CardTitle>
          <CardDescription>
            Créez un compte employé ou utilisateur
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="accountType">Type de compte</Label>
              <Select value={accountType} onValueChange={(value) => setAccountType(value as AccountType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="moderator">Gestionnaire de produits</SelectItem>
                  <SelectItem value="order_manager">Gestionnaire de commandes</SelectItem>
                  <SelectItem value="content_manager">Gestionnaire de contenu</SelectItem>
                  
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="employe@exemple.com"
                required
              />
            </div>
            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Minimum 6 caractères"
                minLength={6}
                required
              />
            </div>
            <Button 
              type="submit" 
              disabled={createAccount.isPending}
              className="w-full sm:w-auto"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              {createAccount.isPending ? "Création..." : "Créer le compte"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Employees list (admin & moderator) */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Employés actifs</CardTitle>
          <CardDescription>
            {employees.length} employé{employees.length > 1 ? 's' : ''}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : employees.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              Aucun employé pour le moment
            </p>
          ) : (
            <div className="space-y-3">
              {employees.map((employee) => (
                  <div
                    key={employee.id}
                    className="flex items-center justify-between p-4 border rounded-lg gap-4"
                  >
                    <div className="flex-1">
                      <p className="font-medium">{employee.email}</p>
                      <p className="text-sm text-muted-foreground">
                        {employee.role === 'admin' 
                          ? 'Administrateur (tous les accès)' 
                          : employee.role === 'order_manager'
                            ? 'Gestionnaire de commandes'
                            : employee.role === 'content_manager'
                              ? 'Gestionnaire de contenu'
                              : 'Gestionnaire de produits'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select
                        value={employee.role}
                        onValueChange={(newRole) => updateRole.mutate({ userId: employee.id, newRole })}
                        disabled={updateRole.isPending}
                      >
                        <SelectTrigger className="w-[230px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white z-50">
                          <SelectItem value="admin">Administrateur</SelectItem>
                          <SelectItem value="order_manager">Gestionnaire de commandes</SelectItem>
                          <SelectItem value="moderator">Gestionnaire de produits</SelectItem>
                          <SelectItem value="content_manager">Gestionnaire de contenu</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => deleteEmployee.mutate(employee.id)}
                        disabled={deleteEmployee.isPending}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </CardContent>
      </Card>


      {/* User details modal */}
      <Dialog open={!!selectedUserId} onOpenChange={(open) => !open && setSelectedUserId(null)}>
        <DialogContent className="max-w-2xl bg-background">
          <DialogHeader>
            <DialogTitle>Détails de l'utilisateur</DialogTitle>
            <DialogDescription>
              Informations complètes du compte
            </DialogDescription>
          </DialogHeader>
          
          {isLoadingDetails ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : userDetails ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <Label className="text-muted-foreground">Email</Label>
                  <p className="font-medium">{userDetails.email}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Rôle</Label>
                  <p className="font-medium">
                    {userDetails.role === 'admin' ? 'Administrateur' : 
                     userDetails.role === 'moderator' ? 'Gestionnaire de produits' :
                     userDetails.role === 'order_manager' ? 'Gestionnaire de commandes' : 'Utilisateur'}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Prénom</Label>
                  <p className="font-medium">{userDetails.profile.first_name || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Nom</Label>
                  <p className="font-medium">{userDetails.profile.last_name || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Téléphone</Label>
                  <p className="font-medium">{userDetails.profile.phone || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Entreprise</Label>
                  <p className="font-medium">{userDetails.profile.company || '-'}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-muted-foreground">Adresse</Label>
                  <p className="font-medium">
                    {userDetails.profile.street_address || '-'}
                    {userDetails.profile.city && `, ${userDetails.profile.city}`}
                    {userDetails.profile.province && `, ${userDetails.profile.province}`}
                    {userDetails.profile.postal_code && ` ${userDetails.profile.postal_code}`}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Compte créé le</Label>
                  <p className="font-medium">
                    {userDetails.created_at 
                      ? new Date(userDetails.created_at).toLocaleDateString('fr-CA', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })
                      : '-'}
                  </p>
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminEmployees;
